import set from 'lodash/set';
import React, { useState, useRef, useCallback } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';
import { graphql } from 'react-relay';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';
import type {
	StarredContentMenuQuery$data,
	StarredContentMenuQuery$variables,
} from './__generated__/StarredContentMenuQuery.graphql';
import uniqBy from 'lodash/uniqBy';
import { NetworkStatus } from 'apollo-client';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { Divider } from '@atlaskit/navigation-system/side-nav-items/menu-section';
import { LinkMenuItem } from '@atlaskit/navigation-system/side-nav-items/link-menu-item';

import { HOME } from '@confluence/named-routes/entry-points/routes';
import { formatTimeAgo } from '@confluence/time-ago';
import { APP_NAV_PAGES_DROPDOWN_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceStop } from '@confluence/experience-tracker/entry-points/component';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback';
import { ViewportObserver } from '@confluence/viewport-observer';
import { useFlyoutActions } from '@confluence/flyout-store';

import { ContentMenu } from './ContentMenu';
import { ContentMenuItem } from './ContentMenuItem';
import emptyStarBox from './assets/emptyStarBox.svg';
import {
	ContentMenuEmptyState,
	ContentMenuLoadingState,
	ContentMenuErrorState,
	ContentMenuNoResultsState,
	ContentMenuLoadMoreState,
} from './ContentMenuStates';
import { ContentMenuTextFilter } from './ContentMenuTextFilter';
import { useInfiniteScroll } from './useInfiniteScroll';

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks'))
			.fireContentMenuItemClickedAnalytics,
);

const StarredContentMenuQuery = graphql`
	query StarredContentMenuQuery($limit: Int = 30, $start: Int!) {
		favoriteContent(limit: $limit, start: $start) {
			nodes {
				id
				type
				subType
				title
				status
				space {
					id
					name
				}
				links {
					webui
					editui
				}
				metadata {
					currentuser {
						favourited {
							isFavourite
							favouritedDate
						}
						lastcontributed {
							status
							when
						}
					}
				}
				emoji: properties(keys: ["emoji-title-published", "emoji-title-draft"]) {
					nodes {
						id
						key
						value
					}
				}
			}
			pageInfo {
				hasNextPage
			}
			edges {
				cursor
			}
		}
	}
`;

export const starredContentMenuQueryVars = { start: 0 };

export const StarredContentMenu = () => {
	const intl = useIntl();
	const [filterText, setFilterText] = useState('');
	const scrollableRef = useRef<HTMLDivElement>(null);
	const { data, loading, error, fetchMore, networkStatus } = useQueryInterop<
		StarredContentMenuQuery$data,
		StarredContentMenuQuery$variables
	>(StarredContentMenuQuery, {
		variables: starredContentMenuQueryVars,
		fetchPolicy: 'cache-and-network',
	});
	const hasNextPage = data?.favoriteContent?.pageInfo?.hasNextPage || false;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { closeFlyout } = useFlyoutActions();

	const lowerFilterText = filterText.toLowerCase();
	const filteredContent = uniqBy(
		data?.favoriteContent?.nodes?.filter(
			(content): content is NonNullable<typeof data.favoriteContent.nodes>[number] =>
				content !== null &&
				content !== undefined &&
				(!filterText || (content.title?.toLowerCase() || '').includes(lowerFilterText)),
		),
		(content) => content?.id,
	);

	const loadMore = useCallback(() => {
		if (networkStatus === NetworkStatus.ready) {
			void fetchMore({
				variables: {
					start: data?.favoriteContent?.nodes?.length || 0,
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prev;
					}
					const prevEdges = prev.favoriteContent?.nodes || [];
					const fetchMoreEdges = fetchMoreResult.favoriteContent?.nodes || [];
					const mergedEdges = [...prevEdges, ...fetchMoreEdges];

					const result = { ...fetchMoreResult };
					set(result, 'favoriteContent.nodes', mergedEdges);
					return result;
				},
			});
		}
	}, [networkStatus, fetchMore, data?.favoriteContent?.nodes]);

	const isEmptyState = !filterText && !filteredContent?.length;

	const fireClickAnalytics = useLazyClickAnalytics(
		createAnalyticsEvent,
		'starredNavigationMenuItem',
		{
			componentType: 'fixedItem',
			itemId: 'viewAllStarredItems',
		},
	);

	const onClick = () => {
		closeFlyout();
		void fireClickAnalytics();
	};

	const {
		showLoadMoreButton,
		setShowLoadMoreButton,
		loadMoreButtonRef,
		handleKeyDown,
		handleBlurCapture,
		handleLoadMoreButtonClick,
	} = useInfiniteScroll({
		hasNextPage,
		scrollableRef,
		loadMore,
	});

	const onFilterTextChange = useCallback(
		(text) => {
			setFilterText(text);
			setShowLoadMoreButton(false);
		},
		[setShowLoadMoreButton],
	);

	return (
		<ContentMenu title={intl.formatMessage(i18n.title)}>
			{!isEmptyState && (
				<Box paddingBlock="space.075" paddingInlineStart="space.050">
					<ContentMenuTextFilter
						filterText={filterText}
						setFilterText={onFilterTextChange}
						placeholder={intl.formatMessage(i18n.filterPlaceholder)}
						isDisabled={loading && !data}
						actionSubjectId="starredNavigationMenuItem"
						itemId="filterStarredItems"
					/>
				</Box>
			)}
			<Box
				xcss={overflowStyles}
				ref={scrollableRef}
				onKeyDown={handleKeyDown}
				onBlurCapture={handleBlurCapture}
			>
				<StarredContentMenuInner
					error={error}
					loading={loading}
					data={data}
					filteredContent={filteredContent}
					filterText={filterText}
				/>
				{!isEmptyState && hasNextPage && data?.favoriteContent?.nodes?.length !== 0 && (
					<div data-testid="starred-content-menu-load-more">
						{showLoadMoreButton ? (
							<Button
								ref={loadMoreButtonRef}
								shouldFitContainer
								onClick={handleLoadMoreButtonClick}
							>
								<FormattedMessage {...i18n.loadMore} />
							</Button>
						) : (
							<>
								<ViewportObserver onViewportEnter={loadMore} />
								<ContentMenuLoadMoreState />
							</>
						)}
					</div>
				)}
			</Box>
			{!isEmptyState && (
				<Box>
					<Divider />
					<LinkMenuItem
						href={HOME.toUrl({ view: 'starred' })}
						elemBefore={<AlignTextLeftIcon label="" color="currentColor" />}
						onClick={onClick}
					>
						{intl.formatMessage(i18n.viewAll)}
					</LinkMenuItem>
				</Box>
			)}
		</ContentMenu>
	);
};

const StarredContentMenuInner = ({ error, loading, data, filteredContent, filterText }) => {
	const intl = useIntl();

	if (error) {
		return (
			<>
				<ContentMenuErrorState errorTitle={intl.formatMessage(i18n.errorMessageTitle)} />
				<ExperienceStop
					name={APP_NAV_PAGES_DROPDOWN_EXPERIENCE}
					error={error}
					attributes={{ navVersion: '4', dropdownType: 'starred' }}
				/>
			</>
		);
	}

	if (loading && !data) {
		return <ContentMenuLoadingState />;
	}

	if (!filteredContent || filteredContent?.length === 0) {
		return filterText ? (
			<ContentMenuNoResultsState />
		) : (
			<ContentMenuEmptyState
				image={emptyStarBox}
				title={intl.formatMessage(i18n.emptyStateTitle)}
				message={intl.formatMessage(i18n.emptyStateMessage)}
			/>
		);
	}

	return (
		<>
			<VisuallyHidden role="status">
				{filterText !== ''
					? intl.formatMessage(i18n.filteredResultsCount, { count: filteredContent.length })
					: intl.formatMessage(i18n.showingAllResults)}
			</VisuallyHidden>
			{filteredContent.map((content) =>
				content ? (
					<ContentMenuItem
						key={content.id}
						content={content}
						actionSubjectId="starredNavigationMenuItem"
						timestamp={
							content.metadata.currentuser?.favourited?.favouritedDate
								? formatTimeAgo(intl, content.metadata.currentuser?.favourited?.favouritedDate)
								: undefined
						}
					/>
				) : null,
			)}
			<ExperienceStop
				name={APP_NAV_PAGES_DROPDOWN_EXPERIENCE}
				attributes={{ navVersion: '4', dropdownType: 'starred' }}
			/>
		</>
	);
};

const overflowStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	overflowY: 'auto',
	height: '100%',
	justifyContent: 'start',
	marginInline: 'space.negative.100',
	paddingInline: 'space.100',
	paddingBlockEnd: 'space.050',
	//@ts-expect-error styling is applying but property does not exist for SafeCSSObject
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		':focus-visible': {
			outlineOffset: `${token('space.negative.025')}`,
		},
	},
});

const i18n = defineMessages({
	title: {
		id: 'side-navigation.starred-content-menu.title',
		defaultMessage: 'Starred',
		description: 'Title for the starred content menu',
	},
	filterPlaceholder: {
		id: 'side-navigation.starred-content-menu.filter-placeholder',
		defaultMessage: 'Filter starred items',
		description: 'Placeholder text for the filter input in the starred content menu',
	},
	viewAll: {
		id: 'side-navigation.starred-content-menu.view-all',
		defaultMessage: 'View all starred items',
		description: 'Text for the view all link in the starred content menu',
	},
	emptyStateTitle: {
		id: 'side-navigation.starred-content-menu.empty-state-title',
		defaultMessage: 'You haven’t starred anything yet',
		description: 'Title for the empty state message in the starred content menu',
	},
	emptyStateMessage: {
		id: 'side-navigation.starred-content-menu.empty-state-message',
		defaultMessage:
			'Mark items that are important to you with a star to quickly access them. You’ll see those items here.',
		description: 'Text for the empty state message in the starred content menu',
	},
	errorMessageTitle: {
		id: 'side-navigation.starred-content-menu.error-message.title',
		defaultMessage: 'We couldn’t load your starred items',
		description: 'Title for the error message in the starred content menu',
	},
	loadMore: {
		id: 'side-navigation.starred-content-menu.load-more',
		defaultMessage: 'Load more',
		description: 'Text for the load more button in the recent content menu',
	},
	filteredResultsCount: {
		id: 'side-navigation.star-content-menu.filtered-results-announcement',
		defaultMessage: '{count, plural, one {# result} other {# results}} found',
		description: 'A total count for the filtered results',
	},
	showingAllResults: {
		id: 'side-navigation.star-content-menu.all-results-announcement',
		defaultMessage: 'Showing all starred pages',
		description: 'Label explaining showing all starred pages visited',
	},
});
